import { BaseGQLService } from '../../common/services/BaseGQLService';
import { ModelType } from '../../common/utils/GQLIdEncoder';
import { ProjectMapper } from '../mappers/ProjectMapper';
import { Project } from '../models/Project';

const PROJECT_FIELDS = `
id
name
description
maxItemCount
defaultProfile {
  id
  name
  type
}
testers {
  edges {
    node {
      id
      name
      preferredName
    }
  }
}
`;

export const GET_PROJECT_QUERY = `
query($projectId: ID!) {
  project: node(id: $projectId) {
    ... on Project {
      ${PROJECT_FIELDS}
    }
  }
}
`;

export const CREATE_PROJECT_QUERY = `
mutation($name: String!, $description: String, $profileId: ID!, $maxItemCount: Int) {
  createProject(
    input: {
      name: $name,
      description: $description,
      maxItemCount: $maxItemCount
      profileId: $profileId,
    }
  ) {
    project {
      ${PROJECT_FIELDS}
    },
  }
}
`;

export const UPDATE_PROJECT_QUERY = `
mutation($projectId: ID!, $name: String, $description: String, $maxItemCount: Int) {
  updateProject(
    input: {
      projectId: $projectId,
      name: $name,
      description: $description
      maxItemCount: $maxItemCount
    }
  ) {
    project {
      ${PROJECT_FIELDS}
    }
  }
}
`;

export const UPDATE_PROJECT_TESTERS_QUERY = `
mutation($projectId: ID!, $testers: [ID!]) {
  updateProject(
    input: {
      projectId: $projectId,
      testers: $testers,
    }
  ) {
    project {
      ${PROJECT_FIELDS}
    }
  }
}
`;

export const DELETE_PROJECT_QUERY = `
mutation($projectId: ID!) {
  deleteProject(
    input: {
      projectId: $projectId
    }
  ) {
    clientMutationId
  }
}
`;

export class ProjectService extends BaseGQLService {
  private readonly mapper = new ProjectMapper();

  async getProject(projectId: number): Promise<Project> {
    return this.query(
      GET_PROJECT_QUERY,
      {
        projectId: this.gqlIdEncoder.encode(ModelType.Project, projectId),
      },
      (data) => this.mapper.mapGQLModel(data.project)
    );
  }

  async createProject(name: string, description: string, profileId: number, maxItemCount: number): Promise<Project> {
    return this.mutate(
      CREATE_PROJECT_QUERY,
      {
        name,
        description,
        profileId: this.gqlIdEncoder.encode(ModelType.Profile, profileId),
        maxItemCount,
      },
      (data) => this.mapper.mapGQLModel(data.createProject.project)
    );
  }

  async updateProject(projectId: number, name: string, description: string, maxItemCount: number): Promise<Project> {
    return this.mutate(
      UPDATE_PROJECT_QUERY,
      {
        projectId: this.gqlIdEncoder.encode(ModelType.Project, projectId),
        name,
        description,
        maxItemCount,
      },
      (data) => this.mapper.mapGQLModel(data.updateProject.project)
    );
  }

  async updateProjectTesters(projectId: number, testerIds: number[]): Promise<Project> {
    return this.mutate(
      UPDATE_PROJECT_TESTERS_QUERY,
      {
        projectId: this.gqlIdEncoder.encode(ModelType.Project, projectId),
        testers: testerIds.map((id) => this.gqlIdEncoder.encode(ModelType.User, id)),
      },
      (data) => this.mapper.mapGQLModel(data.updateProject.project)
    );
  }

  async deleteProject(projectId: number): Promise<void> {
    await this.mutate(DELETE_PROJECT_QUERY, {
      projectId: this.gqlIdEncoder.encode(ModelType.Project, projectId),
    });
  }
}
